import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { Plus, Download, Filter, ArrowUpRight, ArrowDownRight, FileText, Calendar, User } from "lucide-react";
import PaymentForm from "@/components/PaymentForm";
import PaymentAllocationForm from "@/components/PaymentAllocationForm";
import { downloadBalanceSheetCsv } from "@/utils/balanceSheetPdf";

const fmtInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function BalanceSheetPage() {
  const [orgId, setOrgId] = useState("");
  const [customerId, setCustomerId] = useState("");
  
  // Set default date range to last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(today.toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  const { data: orgs = [] } = useQuery({ queryKey: ["orgs"], queryFn: async () => (await api.get("/organizations")).data });
  
  const { data: currentOrg } = useQuery({
    queryKey: ["organization", orgId],
    queryFn: async () => {
      const list = (await api.get("/organizations")).data;
      return list.find((o) => o.id === orgId);
    },
    enabled: !!orgId,
  });
  
  const { data: currentCustomer } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: async () => {
      const list = (await api.get("/customers", { params: { organization_id: orgId } })).data;
      return list.find((c) => c.id === customerId);
    },
    enabled: !!orgId && !!customerId,
  });
  
  const { data: customers = [] } = useQuery({
    queryKey: ["customers", orgId],
    queryFn: async () => (await api.get("/customers", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });

  const { data: ledgerData, isLoading } = useQuery({
    queryKey: ["balance-sheet", { orgId, customerId, dateFrom, dateTo, page }],
    queryFn: async () => (await api.get("/balance-sheet", { 
      params: { 
        organization_id: orgId, 
        customer_id: customerId || undefined, 
        date_from: dateFrom || undefined, 
        date_to: dateTo || undefined,
        page,
        page_size: pageSize
      } 
    })).data,
    enabled: !!orgId,
  });

  const { data: summary } = useQuery({
    queryKey: ["balance-sheet-summary", { orgId, customerId, dateFrom, dateTo }],
    queryFn: async () => (await api.get("/balance-sheet/summary", { 
      params: { 
        organization_id: orgId, 
        customer_id: customerId || undefined, 
        date_from: dateFrom || undefined, 
        date_to: dateTo || undefined
      } 
    })).data,
    enabled: !!orgId,
  });

  const handleAddPayment = () => {
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    // Refresh queries
  };

  const handleAllocatePayment = (paymentId) => {
    setSelectedPaymentId(paymentId);
    setShowAllocationForm(true);
  };

  const handleAllocationSuccess = () => {
    setShowAllocationForm(false);
    setSelectedPaymentId(null);
    // Refresh queries
  };

  const handleExportCsv = async () => {
    try {
      toast.loading("Fetching all records for export...", { id: "export-loading" });
      
      // Fetch all records by paginating through all pages
      const params = {
        organization_id: orgId,
        customer_id: customerId || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page: 1,
        page_size: 100, // Max page size
      };
      
      let allEntries = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await api.get("/balance-sheet", { params: { ...params, page: currentPage } });
        const entries = response.data?.items || [];
        allEntries = [...allEntries, ...entries];
        
        // Check if there are more pages
        const total = response.data?.total || 0;
        hasMore = allEntries.length < total;
        currentPage++;
        
        // Safety limit to prevent infinite loops
        if (currentPage > 1000) {
          console.warn("Stopping export after 1000 pages");
          break;
        }
      }
      
      const csvData = {
        organization: currentOrg,
        customer: currentCustomer,
        entries: allEntries,
        summary: summary,
        dateRange: {
          from: dateFrom,
          to: dateTo,
        },
      };
      
      const customerName = currentCustomer?.name || "All_Customers";
      const dateRangeStr = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : "all_dates";
      const filename = `BalanceSheet_${customerName}_${dateRangeStr}.csv`;
      
      await downloadBalanceSheetCsv(csvData, filename);
      toast.success(`Exported ${allEntries.length} records successfully`, { id: "export-loading" });
    } catch (error) {
      console.error("CSV export failed:", error);
      toast.error("Failed to export CSV", { id: "export-loading" });
    }
  };

  const entries = ledgerData?.items || [];
  const totalPages = Math.max(1, Math.ceil((ledgerData?.total || 0) / pageSize));

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Accounts</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter">Balance Sheet.</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2.5 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-900 font-semibold text-sm"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="border border-slate-200 dark:border-slate-800 p-6 rounded-sm bg-white dark:bg-[#121214]">
            <div className="flex items-center justify-between mb-4">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Total Billed</span>
              <ArrowUpRight className="w-4 h-4 text-red-600" />
            </div>
            <div className="text-2xl font-bold">{fmtInr(summary.total_debit)}</div>
          </div>
          <div className="border border-slate-200 dark:border-slate-800 p-6 rounded-sm bg-white dark:bg-[#121214]">
            <div className="flex items-center justify-between mb-4">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Total Collected</span>
              <ArrowDownRight className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="text-2xl font-bold">{fmtInr(summary.total_credit)}</div>
          </div>
          <div className="border border-slate-200 dark:border-slate-800 p-6 rounded-sm bg-white dark:bg-[#121214]">
            <div className="flex items-center justify-between mb-4">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Total Outstanding</span>
              <span className="text-2xl font-bold text-[#002FA7]">₹</span>
            </div>
            <div className={`text-2xl font-bold ${summary.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {fmtInr(summary.current_balance)}
            </div>
          </div>
          <div className="border border-slate-200 dark:border-slate-800 p-6 rounded-sm bg-white dark:bg-[#121214]">
            <div className="flex items-center justify-between mb-4">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Overdue</span>
              <Calendar className="w-4 h-4 text-orange-600" />
            </div>
            <div className="text-2xl font-bold text-orange-600">{fmtInr(summary.overdue_amount)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1">
          <select
            value={orgId}
            onChange={(e) => { setPage(1); setOrgId(e.target.value); }}
            className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
          >
            <option value="">Select Organization *</option>
            {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
          </select>
        </div>
        <div className="flex-1">
          <select
            value={customerId}
            onChange={(e) => { setPage(1); setCustomerId(e.target.value); }}
            className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
            disabled={!orgId}
          >
            <option value="">All Customers</option>
            {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </div>
        <div className="flex-1">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
            className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
            placeholder="From Date"
          />
        </div>
        <div className="flex-1">
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
            className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
            placeholder="To Date"
          />
        </div>
      </div>

      {/* Ledger Table */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 dark:bg-slate-900 text-left uppercase text-[10px] tracking-[0.2em] text-slate-500">
            <tr>
              <th className="p-4">Date</th>
              <th className="p-4">Description</th>
              <th className="p-4">Customer</th>
              <th className="p-4 text-right">Debit</th>
              <th className="p-4 text-right">Credit</th>
              <th className="p-4 text-right">Outstanding</th>
              <th className="p-4">Due Date</th>
              <th className="p-4">Status</th>
              <th className="p-4">Reference</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (<tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>)}
            {!isLoading && !orgId && (<tr><td colSpan={7} className="p-12 text-center text-slate-500">Select an organization to view balance sheet</td></tr>)}
            {!isLoading && orgId && entries.length === 0 && (
              <tr><td colSpan={7} className="p-16 text-center text-slate-500">
                <FileText className="w-6 h-6 mx-auto mb-3 text-slate-400" />
                No ledger entries found.
              </td></tr>
            )}
            {entries.map((entry, index) => (
              <motion.tr
                key={entry.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className="border-t border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900"
              >
                <td className="p-4 text-slate-600 dark:text-slate-400">{entry.entry_date}</td>
                <td className="p-4 font-medium">{entry.description || '—'}</td>
                <td className="p-4 text-slate-600 dark:text-slate-400">{entry.customer_name || '—'}</td>
                <td className="p-4 text-right">
                  {entry.entry_type === 'debit' ? (
                    <span className="font-semibold text-red-600">{fmtInr(entry.amount)}</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="p-4 text-right">
                  {entry.entry_type === 'credit' ? (
                    <span className="font-semibold text-emerald-600">{fmtInr(entry.amount)}</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="p-4 text-right">
                  {entry.reference_type === 'invoice' ? (
                    <span className={`font-semibold ${entry.outstanding > 0 ? 'text-red-600' : entry.outstanding < 0 ? 'text-emerald-600' : 'text-slate-900 dark:text-white'}`}>
                      {fmtInr(entry.outstanding || 0)}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="p-4 text-slate-600 dark:text-slate-400">
                  {entry.due_date || '—'}
                </td>
                <td className="p-4">
                  {entry.reference_type === 'invoice' ? (
                    <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.15em] ${
                      entry.calculated_status === 'paid' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' :
                      entry.calculated_status === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                      entry.calculated_status === 'cancelled' ? 'bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-300' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                    }`}>
                      {entry.calculated_status || entry.invoice_status || 'pending'}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="p-4">
                  {entry.reference_type === 'invoice' ? (
                    <Link 
                      to={`/app/invoices/${entry.reference_id}`}
                      className="text-[#002FA7] hover:underline font-medium"
                    >
                      {entry.reference_number}
                    </Link>
                  ) : entry.payment_invoice_id ? (
                    <Link 
                      to={`/app/invoices/${entry.payment_invoice_id}`}
                      className="text-[#002FA7] hover:underline font-medium"
                    >
                      {entry.reference_number}
                    </Link>
                  ) : (
                    <span className="text-slate-600 dark:text-slate-400">{entry.reference_number}</span>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
          <span>Page {page} / {totalPages}</span>
          <div className="flex gap-2">
            <button 
              disabled={page <= 1} 
              onClick={() => setPage((p) => p - 1)} 
              className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-sm disabled:opacity-40"
            >
              Prev
            </button>
            <button 
              disabled={page >= totalPages} 
              onClick={() => setPage((p) => p + 1)} 
              className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-sm disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <PaymentForm 
          onClose={() => setShowPaymentForm(false)} 
          onSuccess={handlePaymentSuccess}
          customerId={customerId}
        />
      )}

      {/* Allocation Form Modal */}
      {showAllocationForm && selectedPaymentId && (
        <PaymentAllocationForm 
          paymentId={selectedPaymentId}
          onClose={() => { setShowAllocationForm(false); setSelectedPaymentId(null); }}
          onSuccess={handleAllocationSuccess}
        />
      )}
    </div>
  );
}
